/**
 * One volume control in one small element (Dax, 2026-09-29): a card has room for a knob, not a speaker,
 * a slider and a readout. Drag to turn it (right or up is clockwise); turn it all the way left to mute;
 * double-click to mute or unmute; click the middle to type a level. Arrow keys turn it, M mutes, Enter
 * types. `onChange` reports every step while turning and `done` on release.
 */
import { useEffect, useRef, useState } from 'react';
import { arcPath, KNOB_CLICK_SLOP_PX, KNOB_DOUBLE_CLICK_MS, KNOB_MUTE_GAP_DEG, KNOB_SWEEP_FROM_DEG, KNOB_SWEEP_TO_DEG, knobAngle, knobHeld, knobTurn, knobTyped, type KnobValue } from '@/lib/knob';

/** The dial's drawing box and the radius its arcs run on. */
const BOX = 32;
const RAD = 13;

export function Knob({ value, min, max, label, onChange, disabled, dimmed }: {
  value: KnobValue;
  min: number;
  max: number;
  /** What it controls, for the hover title and the screen reader: "Clip volume". */
  label: string;
  onChange: (v: KnobValue, done: boolean) => void;
  /** Nothing to control yet (an empty channel): greyed and still. */
  disabled?: boolean;
  /**
   * Greyed with a reason on hover, still turning: a clip's knob while its channel is muted in the audio
   * panel keeps its own level, it just is not heard (Dax, 2026-09-30).
   */
  dimmed?: string | null;
}) {
  const range = { min, max };
  const [live, setLive] = useState<KnobValue | null>(null);
  const [editing, setEditing] = useState<string | null>(null);
  const dragRef = useRef<{ x: number; y: number; from: KnobValue; last: KnobValue | null } | null>(null);
  const clickTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const keyRef = useRef<KnobValue | null>(null);
  useEffect(() => () => {
    if (clickTimer.current) clearTimeout(clickTimer.current);
  }, []);

  const v = live ?? value;
  const angle = knobAngle(v, range);
  const pointer = (() => {
    const a = ((angle - 90) * Math.PI) / 180;
    return { x: BOX / 2 + RAD * Math.cos(a), y: BOX / 2 + RAD * Math.sin(a) };
  })();
  const text = v.muted ? 'muted' : `${v.db > 0 ? '+' : ''}${v.db} dB`;

  const commitTyped = () => {
    const typed = editing === null ? null : knobTyped(editing, range);
    setEditing(null);
    if (typed && (typed.db !== value.db || typed.muted !== value.muted)) onChange(typed, true);
  };
  /** A turn over, however it ended: saved if it moved, the dial let go either way. Returns what it was. */
  const endTurn = () => {
    const drag = dragRef.current;
    dragRef.current = null;
    if (drag?.last) {
      onChange(drag.last, true);
      setLive(null);
    }
    return drag;
  };
  const step = (from: KnobValue, by: number): KnobValue => {
    if (from.muted) return by > 0 ? { db: min, muted: false } : from;
    const db = from.db + by;
    return db < min ? { db: min, muted: true } : { db: Math.min(max, db), muted: false };
  };

  return (
    <span className={`knob${v.muted ? ' muted' : ''}${disabled ? ' disabled' : ''}${dimmed ? ' dimmed' : ''}`} role="slider" tabIndex={disabled ? -1 : 0}
      aria-label={label} aria-valuemin={min} aria-valuemax={max} aria-valuenow={v.db} aria-valuetext={text} aria-disabled={disabled || undefined}
      title={live ? undefined : disabled ? label : `${dimmed ? `${dimmed}. ` : ''}${label}: ${text}. Drag to turn, all the way left mutes; double-click mutes; click to type a level`}
      onPointerDown={(e) => {
        // Only the main button turns it: a right-click or a Mac's control-click opens a menu, which keeps the release.
        if (editing !== null || disabled || e.button !== 0 || e.ctrlKey) return;
        // The second press of a double-click mutes or unmutes, and the typing the first one queued never opens.
        if (clickTimer.current) {
          clearTimeout(clickTimer.current);
          clickTimer.current = null;
          onChange({ db: value.db, muted: !value.muted }, true);
          return;
        }
        dragRef.current = { x: e.clientX, y: e.clientY, from: value, last: null };
        e.currentTarget.setPointerCapture(e.pointerId);
      }}
      onPointerMove={(e) => {
        const drag = dragRef.current;
        if (!drag) return;
        // The button is up but the release never reached us: the turn ends here, and this move is not part of it.
        if (!knobHeld(e.buttons)) {
          endTurn();
          if (e.currentTarget.hasPointerCapture(e.pointerId)) e.currentTarget.releasePointerCapture(e.pointerId);
          return;
        }
        const dx = e.clientX - drag.x;
        const dy = e.clientY - drag.y;
        if (!drag.last && Math.abs(dx) + Math.abs(dy) < KNOB_CLICK_SLOP_PX) return;
        const next = knobTurn(drag.from, dx, dy, range);
        drag.last = next;
        setLive(next);
        onChange(next, false);
      }}
      onPointerUp={() => {
        const drag = endTurn();
        if (!drag || drag.last) return;
        clickTimer.current = setTimeout(() => {
          clickTimer.current = null;
          setEditing(value.muted ? '' : String(value.db));
        }, KNOB_DOUBLE_CLICK_MS);
      }}
      onPointerCancel={endTurn}
      onLostPointerCapture={endTurn}
      onKeyDown={(e) => {
        if (editing !== null || disabled) return;
        const by = e.key === 'ArrowUp' || e.key === 'ArrowRight' ? 1 : e.key === 'ArrowDown' || e.key === 'ArrowLeft' ? -1 : 0;
        if (by) {
          e.preventDefault();
          keyRef.current = step(keyRef.current ?? value, by);
          setLive(keyRef.current);
          onChange(keyRef.current, false);
        } else if (e.key === 'Enter') {
          e.preventDefault();
          setEditing(value.muted ? '' : String(value.db));
        } else if (e.key.toLowerCase() === 'm') {
          onChange({ db: value.db, muted: !value.muted }, true);
        }
      }}
      onKeyUp={() => {
        if (!keyRef.current) return;
        onChange(keyRef.current, true);
        keyRef.current = null;
        setLive(null);
      }}>
      <svg viewBox={`0 0 ${BOX} ${BOX}`} aria-hidden="true">
        <path className="knob-track" d={arcPath(BOX / 2, RAD, KNOB_SWEEP_FROM_DEG, KNOB_SWEEP_TO_DEG)} />
        {!v.muted && <path className="knob-level" d={arcPath(BOX / 2, RAD, KNOB_SWEEP_FROM_DEG + KNOB_MUTE_GAP_DEG, Math.max(KNOB_SWEEP_FROM_DEG + KNOB_MUTE_GAP_DEG + 1, angle))} />}
        <circle className="knob-dot" cx={pointer.x} cy={pointer.y} r={2.2} />
      </svg>
      <span className="knob-read" aria-hidden="true">{v.muted ? '×' : v.db}</span>
      {editing !== null && (
        <input autoFocus className="knob-edit" inputMode="decimal" aria-label={`${label} in dB`} value={editing}
          onFocus={(e) => e.currentTarget.select()}
          onChange={(e) => setEditing(e.target.value)}
          onBlur={commitTyped}
          onKeyDown={(e) => {
            e.stopPropagation();
            if (e.key === 'Enter') commitTyped();
            if (e.key === 'Escape') setEditing(null);
          }} />
      )}
    </span>
  );
}
